import * as THREE from 'three'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import type { Quality } from '../types'
import { computePixelRatio } from './resolution'
import { updateThreeDiagnostics } from '../utils/diagnostics'
import {
  AdaptiveQualityController,
  type AdaptiveQualityState,
} from './AdaptiveQuality'
import { shouldUseDioramaEnvironment } from './render-environment'
import { cutsceneOptics } from './cutscene-optics'
import { shouldEnableShadows } from './shadow-policy'

/* eslint-disable @typescript-eslint/no-explicit-any */

// UnrealBloomPass.BlurDirectionX/Y (static Vector2s, absent from the .d.ts). Same values; local copy.
const BLUR_X = new THREE.Vector2(1, 0)
const BLUR_Y = new THREE.Vector2(0, 1)

/** Run ONLY the bloom generation (bright extract → mip blur → composite) of an UnrealBloomPass,
 * leaving the finished bloom in its `renderTargetsHorizontal[0]` and returning that texture. This is
 * UnrealBloomPass.render() steps 1-3 verbatim — same materials, same math, so the glow is identical.
 * What it deliberately SKIPS is step 4: UnrealBloom's final additive blend of the bloom over the
 * full-res scene buffer. That blend is a full-resolution pass, and a SECOND full-res pass (OutputPass)
 * then tone-maps the result. The combine pass below folds the bloom add INTO the tone-map pass, so the
 * frame does one full-res post pass instead of two. */
function generateBloom(renderer: THREE.WebGLRenderer, bloom: UnrealBloomPass, input: THREE.Texture): THREE.Texture {
  const b = bloom as any
  const prevRT = renderer.getRenderTarget()
  const oldClear = new THREE.Color()
  renderer.getClearColor(oldClear)
  const oldAlpha = renderer.getClearAlpha()
  const oldAutoClear = renderer.autoClear
  renderer.autoClear = false
  renderer.setClearColor(0x000000, 0)

  // try/finally so a throw mid-flight (e.g. GPU context loss) can't leave the renderer with
  // autoClear=false + a black clear colour + the bloom RT bound — which would corrupt EVERY
  // subsequent frame until reload. State is always restored.
  try {
    // 1. extract bright areas
    b.highPassUniforms.tDiffuse.value = input
    b.highPassUniforms.luminosityThreshold.value = bloom.threshold
    b.fsQuad.material = b.materialHighPassFilter
    renderer.setRenderTarget(b.renderTargetBright)
    renderer.clear()
    b.fsQuad.render(renderer)

    // 2. blur the mip chain (cheap — all low res)
    let inputRT = b.renderTargetBright
    for (let i = 0; i < b.nMips; i++) {
      b.fsQuad.material = b.separableBlurMaterials[i]
      b.separableBlurMaterials[i].uniforms.colorTexture.value = inputRT.texture
      b.separableBlurMaterials[i].uniforms.direction.value = BLUR_X
      renderer.setRenderTarget(b.renderTargetsHorizontal[i])
      renderer.clear()
      b.fsQuad.render(renderer)
      b.separableBlurMaterials[i].uniforms.colorTexture.value = b.renderTargetsHorizontal[i].texture
      b.separableBlurMaterials[i].uniforms.direction.value = BLUR_Y
      renderer.setRenderTarget(b.renderTargetsVertical[i])
      renderer.clear()
      b.fsQuad.render(renderer)
      inputRT = b.renderTargetsVertical[i]
    }

    // 3. composite the mips (strength/radius/tint baked in here, exactly as UnrealBloom does)
    b.fsQuad.material = b.compositeMaterial
    b.compositeMaterial.uniforms.bloomStrength.value = bloom.strength
    b.compositeMaterial.uniforms.bloomRadius.value = bloom.radius
    b.compositeMaterial.uniforms.bloomTintColors.value = b.bloomTintColors
    renderer.setRenderTarget(b.renderTargetsHorizontal[0])
    renderer.clear()
    b.fsQuad.render(renderer)

    return b.renderTargetsHorizontal[0].texture
  } finally {
    renderer.setClearColor(oldClear, oldAlpha)
    renderer.autoClear = oldAutoClear
    renderer.setRenderTarget(prevRT) // restore caller's target — keeps this helper self-contained
  }
}

/** Owns the WebGLRenderer + a lean post pipeline (HDR scene target → bloom → combine/tone-map). */
export class Renderer {
  readonly renderer: THREE.WebGLRenderer
  private scene: THREE.Scene
  private camera: THREE.Camera
  private sceneRT: THREE.WebGLRenderTarget
  private bloom: UnrealBloomPass
  private combineMat: THREE.RawShaderMaterial
  private combineQuad: FullScreenQuad
  private environmentMap: THREE.Texture
  private adaptive = new AdaptiveQualityController({ targetFps: 144, displayRefreshHz: 144 })
  private adaptiveListener: ((state: AdaptiveQualityState) => void) | null = null
  private quality: Quality
  private usePost = true
  private manualPostEnabled = true
  private manualShadowsEnabled = true
  private baseBloomStrength = 0.85
  /** Keep the title's color pipeline stable while the attract camera changes GPU load. */
  private openingVisualLock = false
  /** Keep cutscene bloom/DOF present for the full screenplay while adaptive quality moves tiers. */
  private cutsceneVisualLock = false
  private w = 1
  private h = 1
  private dprCap = 2
  private baseDprCap = 2 // quality-derived cap; cycleDprCap toggles between this and 1
  private targetH = 0 // chosen render height in physical px; 0 = native (full devicePixelRatio)
  private _lastDpr = 0 // last pixel ratio actually applied; lets resize() skip no-op reallocs
  private _bufSize = new THREE.Vector2()

  constructor(canvas: HTMLCanvasElement, scene: THREE.Scene, camera: THREE.Camera, quality: Quality) {
    this.scene = scene
    this.camera = camera
    this.quality = quality
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      // FALSE on purpose. The scene is drawn to its own multisampled HDR target and the final
      // combine pass is a flat fullscreen quad — context MSAA anti-aliases nothing, it just forces a
      // multisampled default framebuffer + a full-res MSAA resolve every present (very expensive at
      // 4K, zero visual benefit). AA, if wanted, belongs in a post pass, not the context.
      antialias: false,
      powerPreference: 'high-performance',
      stencil: false,
      // MUST stay false — `true` disables the zero-copy page-flip present path (a full-res backbuffer
      // copy every frame, ~half-rate vsync lock at 144Hz). The QA capture paths render synchronously
      // right before toDataURL(), so the drawing buffer is still valid without it.
      preserveDrawingBuffer: false,
    })
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.1
    this.renderer.shadowMap.enabled = quality !== 'low'
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    // The only shadow casters are the maze walls + static decor, and the key light + shadow camera
    // are fixed — so the shadow map is IDENTICAL every frame within a level. Re-rasterizing the whole
    // maze into the 2048² depth map every frame (~21fps at 4K) is pure waste. Freeze it: render once,
    // then reuse. `refreshShadows()` re-renders it on each level rebuild. Dynamic casters (player,
    // jellies, pearls) are excluded from casting so they leave no frozen "ghost" shadow. Zero visible
    // change to the wall shadows, which are the only shadows the static-camera scene actually shows.
    this.renderer.shadowMap.autoUpdate = false
    this.renderer.shadowMap.needsUpdate = true
    this.renderer.info.autoReset = false // accumulate info across our manual passes for diagnostics

    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.environmentMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    scene.environment = this.environmentMap
    scene.environmentIntensity = 0.12
    pmrem.dispose()

    // HDR scene target (linear, HalfFloat) — the scene renders here, then bloom + tone-map read it.
    // generateMipmaps:false + Linear filters — the combine samples it 1:1, so mips are never used;
    // dropping them avoids the mip allocation + any per-frame mip-generation the driver might do.
    this.sceneRT = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      depthBuffer: true,
      stencilBuffer: false,
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      // Antialias actual scene silhouettes; default framebuffer MSAA would only smooth the final quad.
      samples: Math.min(quality === 'high' ? 4 : 2, this.renderer.capabilities.maxSamples),
      resolveDepthBuffer: true,
    })
    this.sceneRT.depthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType)

    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.8, 0.62, 0.82)
    // generateBloom() drives UnrealBloomPass's PRIVATE internals (not in the .d.ts) — verified against
    // three 0.171. The `as any` access defeats TS, so a three bump that renames these would otherwise
    // fail with a cryptic `undefined` crash mid-render. Fail fast at construction with a clear message.
    // Validate EVERY private field generateBloom() dereferences (not just a subset) — else a rename of
    // an unchecked field passes construction and crashes mid-render, defeating the fail-fast intent.
    const bi = this.bloom as unknown as Record<string, unknown>
    if (
      !bi.highPassUniforms || !bi.materialHighPassFilter || !bi.fsQuad || !bi.renderTargetBright ||
      !Array.isArray(bi.separableBlurMaterials) || !Array.isArray(bi.renderTargetsHorizontal) ||
      !Array.isArray(bi.renderTargetsVertical) || typeof bi.nMips !== 'number' ||
      !bi.compositeMaterial || !Array.isArray(bi.bloomTintColors)
    ) {
      throw new Error('UnrealBloomPass internals changed for this three.js version — generateBloom() in Renderer.ts must be updated (three is pinned to ~0.171 for this reason).')
    }

    // Single combine pass: scene + bloom → ACES tone-map → sRGB → screen. The tone-map + colour-space
    // GLSL is copied verbatim from three's OutputShader (ACES branch) so output matches OutputPass
    // exactly; we only add the bloom texture before tone-mapping (which is what UnrealBloom's skipped
    // step 4 did — additive 1:1, with strength already baked into the bloom by its composite pass).
    this.combineMat = new THREE.RawShaderMaterial({
      // Fullscreen present pass: it writes every pixel to the default framebuffer, so depth test/write
      // are pure waste — disable them to skip per-fragment depth work at 4K.
      depthTest: false,
      depthWrite: false,
      uniforms: {
        tDiffuse: { value: null },
        tBloom: { value: null },
        tDepth: { value: this.sceneRT.depthTexture },
        cinematic: { value: 0 },
        focusDistance: { value: 15 },
        sharpRange: { value: 1.3 },
        focusFalloff: { value: 7 },
        maxBlur: { value: 0.009 },
        cameraNear: { value: 0.1 },
        cameraFar: { value: 120 },
        aspect: { value: 1 },
        toneMappingExposure: { value: this.renderer.toneMappingExposure },
      },
      vertexShader: /* glsl */ `
        precision highp float;
        uniform mat4 modelViewMatrix;
        uniform mat4 projectionMatrix;
        attribute vec3 position;
        attribute vec2 uv;
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D tDiffuse;
        uniform sampler2D tBloom;
        uniform sampler2D tDepth;
        uniform float cinematic, focusDistance, sharpRange, focusFalloff, maxBlur;
        uniform float cameraNear, cameraFar, aspect;
        #include <tonemapping_pars_fragment>
        #include <colorspace_pars_fragment>
        varying vec2 vUv;
        float depthAt(vec2 uv) {
          float d = texture2D(tDepth, uv).x;
          return cameraNear * cameraFar / (cameraFar - d * (cameraFar - cameraNear));
        }
        float reefContact(vec2 uv) {
          if (cinematic > 0.5) return 0.0;
          float center = depthAt(uv);
          float occlusion = 0.0;
          // Short-range depth obscurance grounds reef edges without another render target.
          for (int i = 0; i < 8; i++) {
            float angle = float(i) * 2.399963;
            float ring = 0.004 + float(i) * 0.001;
            vec2 p = clamp(uv + vec2(cos(angle) / aspect, sin(angle)) * ring, vec2(0.001), vec2(0.999));
            float delta = center - depthAt(p);
            occlusion += smoothstep(0.08, 0.32, delta) * (1.0 - smoothstep(0.8, 1.6, delta));
          }
          return occlusion / 8.0;
        }
        vec3 miniature(vec2 uv) {
          vec3 color = texture2D(tDiffuse, uv).rgb;
          if (cinematic < 0.5) return color;
          float depth = depthAt(uv);
          float coc = clamp((abs(depth - focusDistance) - sharpRange) / focusFalloff, 0.0, 1.0);
          float radius = coc * maxBlur;
          vec3 sum = color * 2.0;
          float weight = 2.0;
          for (int i = 0; i < 12; i++) {
            float angle = float(i) * 2.399963;
            float ring = sqrt((float(i) + 0.5) / 12.0);
            vec2 offset = vec2(cos(angle) / aspect, sin(angle)) * radius * ring;
            vec2 sampleUv = clamp(uv + offset, vec2(0.001), vec2(0.999));
            // Keep sharp actor silhouettes from bleeding into the defocused background.
            float sampleDepth = depthAt(sampleUv);
            float w = sampleDepth < depth - 1.5 ? 0.05 : 1.0;
            sum += texture2D(tDiffuse, sampleUv).rgb * w;
            weight += w;
          }
          return sum / weight;
        }
        void main() {
          vec3 scene = miniature(vUv);
          scene *= mix(vec3(1.0), vec3(0.35, 0.52, 0.58), reefContact(vUv));
          vec3 bloom = texture2D( tBloom, vUv ).rgb;
          gl_FragColor = vec4( scene + bloom * mix(1.0, 0.2, cinematic), 1.0 );
          gl_FragColor.rgb = ACESFilmicToneMapping( gl_FragColor.rgb );
          float vignette = smoothstep(0.25, 0.8, distance(vUv, vec2(0.5)));
          gl_FragColor.rgb *= 1.0 - cinematic * vignette * 0.12;
          gl_FragColor = sRGBTransferOETF( gl_FragColor );
        }
      `,
    })
    this.combineQuad = new FullScreenQuad(this.combineMat)

    this.applyQuality(quality)
  }

  setSceneCamera(scene: THREE.Scene, camera: THREE.Camera): void {
    this.scene = scene
    this.camera = camera
    const isCutscene = scene.name === 'CutsceneDioramaScene'
    if (this.cutsceneVisualLock !== isCutscene) {
      this.cutsceneVisualLock = isCutscene
      this.applyRuntimeQuality()
    }
    if (shouldUseDioramaEnvironment(this.scene.name)) this.scene.environment ??= this.environmentMap
    this.renderer.shadowMap.autoUpdate = shouldUseDioramaEnvironment(this.scene.name)
    this.refreshShadows()
    this.adaptiveListener?.(this.adaptive.state)
  }

  setAdaptiveQualityListener(listener: (state: AdaptiveQualityState) => void): void {
    this.adaptiveListener = listener
    listener(this.adaptive.state)
  }

  setDisplayRefreshRate(refreshHz: number): void {
    this.adaptive.setDisplayRefreshHz(refreshHz)
    this.applyRuntimeQuality()
  }

  /**
   * Hold the opening screen to one color pipeline. Adaptive quality may still lower
   * render resolution, but it must not switch between the post and direct paths or
   * change bloom strength while the title camera is orbiting.
   */
  setOpeningVisualLock(locked: boolean): void {
    if (this.openingVisualLock === locked) return
    this.openingVisualLock = locked
    this.applyRuntimeQuality()
  }

  updateAdaptiveQuality(dt: number): void {
    if (this.adaptive.sample(dt)) this.applyRuntimeQuality()
  }

  /** Re-render the (otherwise frozen) shadow map once on the next frame. Call after the world's
   * static casters change — i.e. on each level rebuild — so the new maze's wall shadows are captured. */
  refreshShadows(): void {
    this.renderer.shadowMap.needsUpdate = true
  }

  applyQuality(q: Quality): void {
    this.quality = q
    this.dprCap = this.baseDprCap = q === 'high' ? 2 : q === 'medium' ? 1.5 : 1
    this.baseBloomStrength = q === 'high' ? 0.28 : q === 'medium' ? 0.18 : 0
    this.applyRuntimeQuality()
  }

  private applyRuntimeQuality(): void {
    const state = this.adaptive.state
    const visualLock = this.openingVisualLock || this.cutsceneVisualLock
    this.usePost = this.manualPostEnabled && this.quality !== 'low' && (visualLock || state.bloomScale > 0)
    this.bloom.strength = visualLock ? this.baseBloomStrength : this.baseBloomStrength * state.bloomScale
    // Keep shadow enablement stable while adaptive quality changes render scale and detail.
    // Toggling shadowMap.enabled recompiles every lit material and refreshes the frozen map,
    // which presents as shadows flashing on/off on hardware near a quality boundary.
    const shadowsOn = shouldEnableShadows(this.manualShadowsEnabled, this.quality)
    if (this.renderer.shadowMap.enabled !== shadowsOn) {
      // Flipping shadows on/off must recompile materials (add/drop sampling) AND re-raster the frozen
      // map — else e.g. a low→high quality switch leaves the maze unshadowed until the next rebuild.
      this.renderer.shadowMap.enabled = shadowsOn
      this.markSceneMaterialsDirty()
      this.refreshShadows()
    }
    this.adaptiveListener?.(state)
    if (this.w > 1) this.resize(this.w, this.h)
  }

  /** Set the render resolution: target drawing-buffer height in physical px (0 = native panel
   * resolution). Lower values downscale the internal render for performance while the canvas still
   * fills the viewport; re-applies immediately. */
  setResolution(targetHeight: number): void {
    // Coerce defensively — a corrupt/tampered persisted setting (non-numeric string, NaN) must not
    // reach computePixelRatio, where it would yield NaN and corrupt the drawing buffer for the session.
    const h = Number(targetHeight)
    this.targetH = Number.isFinite(h) ? Math.max(0, Math.round(h)) : 0
    if (this.w > 1) this.resize(this.w, this.h)
  }

  resize(w: number, h: number): void {
    const requestedDpr = computePixelRatio(this.targetH, h, window.devicePixelRatio || 1, this.dprCap)
    const minimumDpr = Math.min(0.5, requestedDpr)
    const dpr = Math.min(requestedDpr, Math.max(minimumDpr, requestedDpr * this.adaptive.state.renderScale))
    // Skip the GPU-texture realloc when nothing affecting the drawing buffer changed — a fullscreen
    // toggle or a continuous window drag fires many resize events/sec, and WebGLRenderTarget.setSize
    // reallocates even for an identical size. A targetH/quality change alters dpr, so it is never skipped.
    if (w === this.w && h === this.h && dpr === this._lastDpr) return
    this.w = w
    this.h = h
    this._lastDpr = dpr
    this.renderer.setPixelRatio(dpr)
    this.renderer.setSize(w, h, false)
    this.renderer.getDrawingBufferSize(this._bufSize)
    this.sceneRT.setSize(this._bufSize.x, this._bufSize.y)
    // Bloom runs at quarter the logical resolution — it's a wide blur, so this is visually identical
    // while making its mip chain cheap. (setSize takes logical px; UnrealBloom halves again internally.)
    this.bloom.setSize(Math.max(1, Math.round(w / 2)), Math.max(1, Math.round(h / 2)))
    // NB: deliberately do NOT refreshShadows() here. The shadow map is a fixed 2048² ortho render of
    // the world — resolution-independent — so a canvas/DPR resize does not invalidate it. Refreshing on
    // every resize would re-raster the whole maze each frame during a continuous window drag, defeating
    // the freeze. Shadow-invalidating changes (level rebuild, shadow enable/disable) refresh explicitly.
  }

  render(): void {
    if (import.meta.env.DEV) this.renderer.info.reset()
    const optics = cutsceneOptics(this.scene.name, this.scene.userData.focusDistance)
    this.renderer.toneMappingExposure = optics.enabled ? 1.18 : 1.1
    const uniforms = this.combineMat.uniforms
    uniforms.cinematic.value = optics.enabled ? 1 : 0
    uniforms.focusDistance.value = optics.focusDistance
    uniforms.sharpRange.value = optics.sharpRange
    uniforms.focusFalloff.value = optics.falloff
    uniforms.maxBlur.value = optics.maxBlur
    if (this.camera instanceof THREE.PerspectiveCamera) {
      uniforms.cameraNear.value = this.camera.near
      uniforms.cameraFar.value = this.camera.far
      uniforms.aspect.value = this.camera.aspect
    }
    if (this.usePost) {
      // 1. scene → HDR target (linear; three applies NoToneMapping when drawing to a render target)
      this.renderer.setRenderTarget(this.sceneRT)
      this.renderer.clear()
      this.renderer.render(this.scene, this.camera)
      // 2. bloom-only (cheap, low res)
      const bloomTex = generateBloom(this.renderer, this.bloom, this.sceneRT.texture)
      // 3. combine (scene + bloom) + tone-map + sRGB → screen, in ONE full-res pass
      this.combineMat.uniforms.tDiffuse.value = this.sceneRT.texture
      this.combineMat.uniforms.tBloom.value = bloomTex
      this.combineMat.uniforms.toneMappingExposure.value = this.renderer.toneMappingExposure
      this.renderer.setRenderTarget(null)
      this.combineQuad.render(this.renderer)
    } else {
      // Low quality: no bloom — render straight to screen (renderer applies tone-map + sRGB).
      this.renderer.setRenderTarget(null)
      this.renderer.render(this.scene, this.camera)
    }
    if (!import.meta.env.DEV) return
    const info = this.renderer.info
    updateThreeDiagnostics({
      ready: true,
      calls: info.render.calls,
      triangles: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      programs: info.programs?.length ?? 0,
      dpr: this.renderer.getPixelRatio(),
      bufW: this._bufSize.x,
      bufH: this._bufSize.y,
      qualityTier: this.adaptive.state.tier,
      renderScale: this.adaptive.state.renderScale,
      adaptiveTargetFps: this.adaptive.state.targetFps,
      displayRefreshHz: this.adaptive.state.displayRefreshHz,
      detailFraction: this.adaptive.state.detailFraction,
      postEnabled: this.usePost,
      shadowsEnabled: this.renderer.shadowMap.enabled,
    })
  }

  // --- Dev-only perf-bisection toggles (wired to keys in GameApp; zero production impact). ---
  /** Toggle the post chain (bloom + tone-map combine). Returns the new state. */
  togglePost(): boolean {
    this.manualPostEnabled = !this.manualPostEnabled
    this.applyRuntimeQuality()
    return this.usePost
  }

  /** Flag every scene material for recompile — needed when shadow sampling is added/dropped (three
   * keys the program on shadowMap.enabled but won't recompile an already-compiled material on its own). */
  private markSceneMaterialsDirty(): void {
    this.scene.traverse((o) => {
      const mat = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined
      if (Array.isArray(mat)) mat.forEach((m) => (m.needsUpdate = true))
      else if (mat) mat.needsUpdate = true
    })
  }

  /** Toggle shadow rendering. Materials must recompile to add/drop shadow sampling. */
  toggleShadows(): boolean {
    this.manualShadowsEnabled = !this.manualShadowsEnabled
    this.applyRuntimeQuality()
    return this.renderer.shadowMap.enabled
  }

  /** Flip the DPR cap between the quality-derived cap and 1 to isolate fill-rate/resolution cost. */
  cycleDprCap(): number {
    this.dprCap = this.dprCap > 1 ? 1 : this.baseDprCap
    if (this.w > 1) this.resize(this.w, this.h)
    return this.dprCap
  }

  dispose(): void {
    this.sceneRT.dispose()
    this.bloom.dispose()
    this.combineMat.dispose()
    this.combineQuad.dispose()
    this.environmentMap.dispose()
    this.renderer.dispose()
  }
}
